/**
 * AuthShell — shared chrome for Login / Signup / ForgotPassword.
 *
 * Apple-quiet: monochrome canvas with a subtle primary-tinted radial wash that
 * reads premium in dark mode and clean in light mode. Single accent (primary)
 * for the brand mark; everything else relies on token surfaces. Centered card
 * uses shadcn Card so border, radius, shadow, and dark-mode all "just work".
 */

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AicostrMark } from './icons/AicostrMark';
import { Card, CardContent } from '@/components/ui/card';

interface AuthShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Footer line such as "Don't have an account? Sign up". */
  footer?: React.ReactNode;
  /** Visually wider card for the signup form. */
  size?: 'default' | 'wide';
}

export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  size = 'default',
}: AuthShellProps) {
  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {/* Subtle ambient wash — primary-tinted in the corner for a hint of */}
      {/* color without sliding into "1990s gradient SaaS". */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 size-[40rem] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-40 right-0 size-[30rem] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-12">
        {/* Brand */}
        <Link
          to="/"
          className="group mb-10 inline-flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground transition-opacity hover:opacity-80"
          aria-label="aicostr home"
        >
          <AicostrMark size={32} />
          <span><span className="text-primary">ai</span>costr</span>
        </Link>

        <Card
          className={cn(
            'w-full border-hairline bg-card/95 shadow-md backdrop-blur-md',
            size === 'wide' ? 'max-w-md' : 'max-w-[26rem]',
          )}
        >
          <CardContent className="px-7 pb-7 pt-7">
            <div className="mb-6 space-y-1.5">
              <h1 className="text-xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              )}
            </div>
            {children}
          </CardContent>
        </Card>

        {footer && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {footer}
          </p>
        )}
      </div>
    </div>
  );
}
